import api from '../api/axios';
import { FileDataService } from './fileDataService';

export interface DocumentType {
  id: number;
  name: string;
}

export interface ChildDocument {
  id: number;
  unique_id: string;
  child_id: number;
  display_name: string;
  description: string | null;
  document_date: string | null;
  document_type_id: number;
  document_type_name: string;
  updated_at: string;
  // from the joined upload
  upload_id: number;
  file_name: string;
  file_type: string;
  uploaded_at: string;
  created_by_name: string | null;
}

export interface ChildDocumentMetadata {
  display_name?: string;
  description?: string | null;
  document_date?: string | null;
  document_type_id?: number;
}

export class ChildDocumentDataService {
  static async listTypes(): Promise<DocumentType[]> {
    const res = await api.get('/child-documents/types');
    return res.data as DocumentType[];
  }

  static async list(childId: number): Promise<ChildDocument[]> {
    const res = await api.get(`/child-documents?childId=${childId}`);
    return res.data as ChildDocument[];
  }

  /** Short-lived inline URL backing the in-app PDF.js viewer. */
  static async getViewUrl(id: number): Promise<string> {
    const res = await api.get(`/child-documents/${id}/view`);
    return res.data.url as string;
  }

  /** Attachment URL for a normal download. Staff only (server-enforced). */
  static async getDownloadUrl(id: number): Promise<string> {
    const res = await api.get(`/child-documents/${id}/download`);
    return res.data.url as string;
  }

  static async update(id: number, metadata: ChildDocumentMetadata): Promise<void> {
    await api.put(`/child-documents/${id}`, metadata);
  }

  static async remove(id: number): Promise<void> {
    await api.delete(`/child-documents/${id}`);
  }

  /**
   * Full upload flow for one PDF: get a presigned PUT URL, upload straight to
   * S3, then create the upload + child_document rows. Admin only (server-enforced).
   */
  static async upload(
    child: { id: number; unique_id: string },
    file: File,
    displayName: string,
    documentTypeId?: number,
  ): Promise<void> {
    if (file.type !== 'application/pdf') {
      throw new Error('Only PDF documents are supported');
    }
    const { signedUrl, fileKey } = await FileDataService.getPresignedUrl(
      file.name,
      file.type,
      child.unique_id,
      'document',
    );
    await FileDataService.uploadToS3(signedUrl, file);
    await api.post('/child-documents', {
      child_id: child.id,
      display_name: displayName,
      document_type_id: documentTypeId ?? null,
      upload: { file_name: file.name, file_type: file.type, s3_key: fileKey },
    });
  }
}
